/**
 * Runtime utilities for theme management
 * These are framework-agnostic utilities that work in any browser environment.
 */

import { type ThemeId, defaultThemeId, normalizeThemeId, themes } from "@smallbiz/design-tokens";

// ============================================================================
// STORAGE KEYS
// ============================================================================

const STORAGE_KEY_THEME = "sbi-theme-variant";
const STORAGE_KEY_DARK_MODE = "sbi-dark-mode";

// ============================================================================
// RUNTIME UTILITIES
// ============================================================================

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Apply a theme to the document root
 *
 * Sets the data-theme attribute which triggers CSS variable changes.
 * Also persists the preference to localStorage.
 *
 * @param themeId - The theme to apply
 */
export function applyTheme(themeId: ThemeId): void {
  if (!isBrowser()) return;

  const root = document.documentElement;

  // Set the data-theme attribute - CSS will handle all color changes
  root.setAttribute("data-theme", themeId);

  // Also set data-theme-variant for backward compatibility
  root.setAttribute("data-theme-variant", themeId);

  // Store theme preference
  try {
    localStorage.setItem(STORAGE_KEY_THEME, themeId);
  } catch {
    // localStorage might be unavailable (private browsing, etc.)
  }
}

/**
 * Get the current theme from localStorage or return default
 *
 * @returns The current theme ID
 */
export function getCurrentTheme(): ThemeId {
  if (!isBrowser()) return defaultThemeId;

  try {
    const stored = localStorage.getItem(STORAGE_KEY_THEME);
    // #3071 S1: a legacy id (storm, canvas, …) reads as its new id, and the new id is stored once,
    // so the next read is a plain match.
    const themeId = normalizeThemeId(stored);
    if (themeId) {
      if (themeId !== stored) localStorage.setItem(STORAGE_KEY_THEME, themeId);
      return themeId;
    }
  } catch {
    // localStorage might be unavailable
  }

  return defaultThemeId;
}

/**
 * Check if a string is a valid theme ID
 */
export function isValidThemeId(value: string): value is ThemeId {
  // #3071 S1: current ids only; a legacy id goes through normalizeThemeId.
  return normalizeThemeId(value) === value;
}

/**
 * Get theme metadata
 */
export function getTheme(themeId: ThemeId) {
  return themes[themeId];
}

// ============================================================================
// DARK MODE UTILITIES
// ============================================================================

/**
 * Check if dark mode is currently active
 */
export function isDarkMode(): boolean {
  if (!isBrowser()) return false;
  return document.documentElement.classList.contains("dark");
}

/**
 * Set dark mode on/off
 */
export function setDarkMode(enabled: boolean): void {
  if (!isBrowser()) return;

  const root = document.documentElement;

  if (enabled) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }

  try {
    localStorage.setItem(STORAGE_KEY_DARK_MODE, enabled ? "true" : "false");
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Toggle dark mode
 */
export function toggleDarkMode(): boolean {
  const newState = !isDarkMode();
  setDarkMode(newState);
  return newState;
}

/**
 * Get dark mode preference from storage
 */
export function getDarkModePreference(): boolean | null {
  if (!isBrowser()) return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY_DARK_MODE);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // localStorage might be unavailable
  }

  return null;
}

/**
 * Initialize dark mode based on stored preference or system preference
 */
export function initializeDarkMode(): void {
  if (!isBrowser()) return;

  const stored = getDarkModePreference();

  if (stored !== null) {
    setDarkMode(stored);
  } else {
    // Check system preference
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    setDarkMode(prefersDark);
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the theme system
 *
 * Should be called early in the app lifecycle to restore saved preferences.
 */
export function initializeTheme(): void {
  if (!isBrowser()) return;

  // Apply saved theme
  const savedTheme = getCurrentTheme();
  applyTheme(savedTheme);

  // Initialize dark mode
  initializeDarkMode();
}

// ============================================================================
// CSS GENERATION (for build-time use)
// ============================================================================

/**
 * Generate CSS custom properties for a theme
 *
 * Used for generating theme CSS or for inline styles.
 */
export function generateThemeCSSVars(
  themeId: ThemeId,
  mode: "light" | "dark" = "light",
): Record<string, string> {
  const theme = themes[themeId];
  const colors = mode === "dark" ? theme.dark : theme.light;

  return {
    "--theme-primary": colors.primary,
    "--theme-accent": colors.accent,
    "--primary": colors.primary,
    "--primary-foreground": colors.primaryForeground,
    "--accent": colors.accent,
    "--accent-foreground": colors.accentForeground,
    "--background": colors.background,
    "--foreground": colors.foreground,
    "--surface": colors.surface,
    "--surface-alt": colors.surfaceAlt,
    "--border": colors.border,
    "--muted": colors.muted,
    "--muted-foreground": colors.mutedForeground,
  };
}

/**
 * Generate a CSS string for a theme (light mode)
 */
export function generateThemeCSSString(themeId: ThemeId): string {
  const lightVars = generateThemeCSSVars(themeId, "light");
  const darkVars = generateThemeCSSVars(themeId, "dark");

  const lightEntries = Object.entries(lightVars)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join("\n");

  const darkEntries = Object.entries(darkVars)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join("\n");

  return `[data-theme='${themeId}'] {\n${lightEntries}\n}\n\n[data-theme='${themeId}'].dark {\n${darkEntries}\n}`;
}
